import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Bar, Line } from 'react-chartjs-2';
import { FaCog } from 'react-icons/fa';

import ViewComparisonTabs from '../ViewComparisonTabs';
import { formatPatientCount, formatSiteCount, formatHCPCount } from '../../../../utils/MathUtils';
import { getCciCategory, getComparisonColor } from './utils';

const SystemOverviewSection = ({
  selectedSystem,
  activeComparison,
  onComparisonChange,
  onOpenSettings,
  profile,
  comparisonAvgLabel,
  comparisonAvgSuffix,
  cciAdjusted,
  unmetNeedsApiData,
  unmetNeedsDisplayMap,
  exacerbationTrendsApiData,
}) => {
  const comparisonProfile = profile?.[comparisonAvgSuffix] ?? {};
  const unmetNeedsData = useMemo(() => Array.isArray(unmetNeedsApiData) ? unmetNeedsApiData : [], [unmetNeedsApiData]);
  const exacerbationData = useMemo(() => Array.isArray(exacerbationTrendsApiData) ? exacerbationTrendsApiData : [], [exacerbationTrendsApiData]);

  const unmetNeedsChartData = useMemo(() => ({
    labels: unmetNeedsData.map((item) => unmetNeedsDisplayMap[item.name] || item.name),
    datasets: [
      {
        label: 'Your System',
        data: unmetNeedsData.map((item) => item.system),
        backgroundColor: unmetNeedsData.map((item) =>
          item.system > item[comparisonAvgSuffix] ? 'rgba(255, 99, 132, 0.8)' : 'rgba(75, 192, 192, 0.8)'
        ),
        borderColor: unmetNeedsData.map((item) =>
          item.system > item[comparisonAvgSuffix] ? 'rgba(255, 99, 132, 1)' : 'rgba(75, 192, 192, 1)'
        ),
        borderWidth: 1,
        pointStyle: 'circle',
      },
    ],
  }), [unmetNeedsData, unmetNeedsDisplayMap, comparisonAvgSuffix]);

  const unmetNeedsChartOptions = useMemo(
    () => ({
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        title: {
          display: false,
          text: `Key Unmet Needs by % of Patients${cciAdjusted ? ' (CCI Adjusted)' : ''}`,
          font: { size: 16 },
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const datasetLabel = context.dataset.label || '';
              const value = context.parsed.x || 0;
              const index = context.dataIndex;
              const apiItem = unmetNeedsData[index];
              const comparisonAvg = apiItem?.[comparisonAvgSuffix] ?? 0;
              return [
                `${datasetLabel}: ${value.toFixed(1)}% of patients`,
                `${comparisonAvgLabel} Average: ${comparisonAvg.toFixed(1)}%`,
                `${value > comparisonAvg ? 'Above' : 'Below'} ${comparisonAvgLabel} average`,
              ];
            },
          },
        },
        datalabels: {
          display: true,
          anchor: 'end',
          align: 'right',
          offset: -5,
          formatter: (value) => `${value.toFixed(1)}%`,
          color: '#333',
          font: {
            weight: 'bold',
          },
        },
      },
      scales: {
        x: { beginAtZero: true, max: 100, title: { display: true, text: '% of Patients' } },
        y: {
          ticks: {
            font: { size: 13 },
            color: '#000000',
          },
        },
      },
    }),
    [unmetNeedsData, comparisonAvgLabel, comparisonAvgSuffix, cciAdjusted]
  );

  const exacerbationTrendsChartData = useMemo(
    () => ({
      labels: exacerbationData.map((item) => `${item.quarter} 2023`),
      datasets: [
        {
          label: 'Your System',
          data: exacerbationData.map((item) => parseFloat(item.system)),
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.5)',
          tension: 0.1,
          fill: false,
        },
        {
          label: `${comparisonAvgLabel} Average`,
          data: exacerbationData.map((item) => parseFloat(item[comparisonAvgSuffix])),
          borderColor: 'rgb(255, 152, 0)',
          backgroundColor: 'rgba(255, 152, 0, 0.5)',
          tension: 0.1,
          fill: false,
          borderDash: [5, 5],
        },
      ],
    }),
    [exacerbationData, comparisonAvgLabel, comparisonAvgSuffix]
  );

  const exacerbationTrendsOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        legend: { position: 'top' },
        title: { display: false, text: 'Trends in Exacerbations by Quarter', font: { size: 16 } },
        tooltip: { enabled: true, mode: 'index', intersect: false },
        datalabels: {
          display: true,
          anchor: 'end',
          align: 'top',
          formatter: (value) => `${value.toFixed(1)}%`,
          font: { weight: 'bold' },
          color: '#555',
        },
      },
      scales: {
        x: { grid: { display: true } },
        y: {
          grid: { display: true },
          beginAtZero: true,
          min: 0,
          ticks: { callback: (value) => `${value}%` },
          title: { display: true, text: 'Exacerbation Rate (%)' },
        },
      },
      elements: { line: { tension: 0.1 }, point: { radius: 3 } },
      hover: { mode: null, animationDuration: 0 },
      responsiveAnimationDuration: 0,
      events: ['click'],
    }),
    []
  );

  const getSafeNumber = (value) => {
    if (typeof value === 'number') {
      return value;
    }
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  return (
    <div className="system-profile-container">
      <div className="system-profile-header">
        <h3>Health System Profile: {selectedSystem.name}</h3>
        <div className="flex items-center">
          <ViewComparisonTabs
            activeComparison={activeComparison}
            onComparisonChange={onComparisonChange}
            availableLevels={['local', 'state', 'national']}
          />
          <button
            onClick={onOpenSettings}
            className="settings-button p-2 ml-4 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Settings"
          >
            <FaCog className="text-xl text-gray-600" />
          </button>
        </div>
      </div>

      <div className="profile-stats-container grid grid-cols-1 gap-4 mb-6">
        <div className="profile-summary p-4 bg-white rounded-lg shadow">
          <h4 className="text-lg font-semibold mb-4 border-b pb-2">System Overview</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="stat-item">
              <div className="stat-value text-xl font-bold">{formatPatientCount(profile?.total_patients)}</div>
              <div className="stat-label text-sm text-gray-600">Total Patients</div>
            </div>
            <div className="stat-item">
              <div className="stat-value text-xl font-bold">{formatSiteCount(profile?.num_sites)}</div>
              <div className="stat-label text-sm text-gray-600">Number of Sites</div>
            </div>
            <div className="stat-item">
              <div className="stat-value text-xl font-bold">{formatHCPCount(profile?.num_hcps)}</div>
              <div className="stat-label text-sm text-gray-600">Number of HCPs</div>
            </div>
            <div className="stat-item">
              <div className="stat-value text-xl font-bold">{getSafeNumber(profile?.pct_commercial).toFixed(1)}%</div>
              <div className="stat-label text-sm text-gray-600">
                Commercial
                <span
                  className={`ml-1 ${getComparisonColor(
                    profile?.pct_commercial,
                    comparisonProfile?.pct_commercial,
                    false
                  )}`}
                >
                  ({getSafeNumber(comparisonProfile?.pct_commercial).toFixed(1)}% in {comparisonAvgLabel} average)
                </span>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-value text-xl font-bold">{getSafeNumber(profile?.pct_medicare).toFixed(1)}%</div>
              <div className="stat-label text-sm text-gray-600">
                Medicare
                <span
                  className={`ml-1 ${getComparisonColor(
                    profile?.pct_medicare,
                    comparisonProfile?.pct_medicare,
                    true
                  )}`}
                >
                  ({getSafeNumber(comparisonProfile?.pct_medicare).toFixed(1)}% in {comparisonAvgLabel} average)
                </span>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-value text-xl font-bold">{getSafeNumber(profile?.pct_medicaid).toFixed(1)}%</div>
              <div className="stat-label text-sm text-gray-600">
                Medicaid
                <span
                  className={`ml-1 ${getComparisonColor(
                    profile?.pct_medicaid,
                    comparisonProfile?.pct_medicaid,
                    true
                  )}`}
                >
                  ({getSafeNumber(comparisonProfile?.pct_medicaid).toFixed(1)}% in {comparisonAvgLabel} average)
                </span>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-value text-xl font-bold">{getSafeNumber(profile?.avg_age).toFixed(1)}</div>
              <div className="stat-label text-sm text-gray-600">
                Avg. Age
                <span
                  className={`ml-1 ${getComparisonColor(
                    profile?.avg_age,
                    comparisonProfile?.avg_age,
                    true
                  )}`}
                >
                  ({getSafeNumber(comparisonProfile?.avg_age).toFixed(1)} in {comparisonAvgLabel} average)
                </span>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-value text-xl font-bold">
                {getSafeNumber(profile?.avg_cci).toFixed(1)}{' '}
                <span className="text-base font-normal text-gray-600">({getCciCategory(profile?.avg_cci)})</span>
              </div>
              <div className="stat-label text-sm text-gray-600">
                Charlson Index
                <span
                  className={`ml-1 ${getComparisonColor(
                    profile?.avg_cci,
                    comparisonProfile?.avg_cci,
                    true
                  )}`}
                >
                  ({getSafeNumber(comparisonProfile?.avg_cci).toFixed(1)} in {comparisonAvgLabel} average)
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="profile-stats-container grid grid-cols-1 md:grid-cols-1 gap-4 mb-6">
        <div className="profile-chart unmet-needs-chart">
          <h4 className="text-lg font-semibold mb-2">Key Unmet Needs</h4>
          <div className="chart-description">
            <p>
              This chart shows the percentage of patients with each unmet need. Comparing to
              <strong> {comparisonAvgLabel} average</strong>.
              <span className="color-legend">
                <span className="color-indicator red"></span> Above {comparisonAvgLabel} average
                <span className="color-indicator green"></span> Below {comparisonAvgLabel} average
              </span>
            </p>
          </div>
          <div style={{ height: '224px', position: 'relative' }}>
            <Bar data={unmetNeedsChartData} options={unmetNeedsChartOptions} />
          </div>
        </div>

        <div className="profile-chart exacerbation-trends-chart">
          <h4 className="text-lg font-semibold mb-2">Trends in Exacerbations by Quarter</h4>
          <div className="chart-description">
            <p>
              This chart shows the trends in asthma exacerbation rates over the last four quarters. The system's rates are
              compared against {comparisonAvgLabel} averages.
            </p>
          </div>
          <div style={{ height: '210px', position: 'relative' }}>
            <Line data={exacerbationTrendsChartData} options={exacerbationTrendsOptions} />
          </div>
        </div>
      </div>
    </div>
  );
};

SystemOverviewSection.propTypes = {
  selectedSystem: PropTypes.shape({
    name: PropTypes.string.isRequired,
  }).isRequired,
  activeComparison: PropTypes.string.isRequired,
  onComparisonChange: PropTypes.func.isRequired,
  onOpenSettings: PropTypes.func.isRequired,
  profile: PropTypes.object,
  comparisonAvgLabel: PropTypes.string.isRequired,
  comparisonAvgSuffix: PropTypes.string.isRequired,
  cciAdjusted: PropTypes.bool.isRequired,
  unmetNeedsApiData: PropTypes.array,
  unmetNeedsDisplayMap: PropTypes.object.isRequired,
  exacerbationTrendsApiData: PropTypes.array,
};

export default SystemOverviewSection;
